import React, { useState, useEffect } from 'react';
import keyBy from 'lodash/keyBy';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
// ---
import { PinyinEditor } from './with-pinyin/PinyinEditor';
import { withInjectedValue } from './with-injected-value';
import { useDraftEditorStateValue } from './utils/use-draft-editor-state-value';
import { PinyinToolbar } from './with-pinyin/PinyinToolbar';

export const withPinyin = (DraftEditorComponent) => {
  const InjectedDraftEditor = withInjectedValue(DraftEditorComponent);

  const WithPinyinEditor = (props) => {
    const {
      value: propValue,
      defaultFontSize = 24,
      toolbarCustomButtons: toolbarCustomButtonsProp = [],
    } = props;

    const { value, setValue, draftEditorState, setDraftEditorState } =
      useDraftEditorStateValue(props);

    const [pinyinMap, setPinyinMap] = useState(
      propValue?.spacedPinyin ? keyBy(propValue.spacedPinyin, 'index') : undefined
    );

    const [isPinyinEditMode, setIsPinyinEditMode] = useState(false);

    const handleDraftEditorStateChange = (draftEditorState) => {
      setDraftEditorState(draftEditorState);
    };

    const handleChange = (value) => {
      setValue(value);
    };

    const handlePinyinEditorChange = (pinyinMap) => {
      setPinyinMap(pinyinMap);
    };

    const turnOnPinyinEditMode = () => {
      setIsPinyinEditMode(true);
    };

    const turnOffPinyinEditMode = () => {
      setIsPinyinEditMode(false);
    };

    useEffect(() => {
      setValue((prevValue) => {
        if (!prevValue && isEmpty(pinyinMap)) {
          return prevValue;
        }

        return {
          ...prevValue,
          spacedPinyin: Object.values(pinyinMap),
        };
      });
    }, [pinyinMap]);

    const toolbarCustomButtons = [
      <PinyinToolbar key="edit-pinyin" onEditPinyin={turnOnPinyinEditMode} />,
      ...toolbarCustomButtonsProp,
    ];

    return (
      <>
        {!isPinyinEditMode && (
          <InjectedDraftEditor
            {...props}
            toolbarCustomButtons={toolbarCustomButtons}
            value={value}
            onDraftEditorStateChange={handleDraftEditorStateChange}
            onChange={handleChange}
          />
        )}

        <PinyinEditor
          defaultFontSize={defaultFontSize}
          isEditing={isPinyinEditMode}
          draftEditorState={draftEditorState}
          pinyinMap={pinyinMap}
          onChange={handlePinyinEditorChange}
          onClose={turnOffPinyinEditMode}
        />
      </>
    );
  };

  WithPinyinEditor.propTypes = {
    ...InjectedDraftEditor.propTypes,
    draftEditorState: PropTypes.object,
    onDraftEditorStateChange: PropTypes.func,
    value: PropTypes.shape({
      html: PropTypes.string.isRequired,
      spacedPinyin: PropTypes.arrayOf(
        PropTypes.shape({
          index: PropTypes.number.isRequired,
          span: PropTypes.number.isRequired,
          pinyin: PropTypes.string,
        })
      ),
    }),
  };

  const PossiblyWithPinyinEditor = (props) => {
    const { withPinyin: withPinyinProp, ...draftEditorProps } = props;

    if (!withPinyinProp) {
      return <InjectedDraftEditor {...draftEditorProps} />;
    }

    return <WithPinyinEditor {...draftEditorProps} />;
  };

  PossiblyWithPinyinEditor.propTypes = {
    ...WithPinyinEditor.propTypes,
    withPinyin: PropTypes.bool,
  };

  PossiblyWithPinyinEditor.isValueInjected = true;

  return PossiblyWithPinyinEditor;
};
